import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useResizeDetector } from 'react-resize-detector';

import { IconChevronDown, IconTruck } from 'assets/icons';
import { DELIVERY_PATH } from 'constants/path';
import {
  FixedTicket,
  UnfixedTicket,
  ScheduleState,
  BookingConnection,
  BookingTicket,
  TicketState,
  BookingEdge,
  FixedBookingTicket,
} from 'types/api';
import { Option } from 'types/common.types';
import { useIsMobile } from 'hooks/common';
import RefreshButton from 'components/RefreshButton/RefreshButton';
import Loading from 'components/Loading/Loading';
import { MeData } from 'graphQL/Auth/types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';

import { useBookingListHooks } from './hooks/useBookingListHooks';

import SelectingMonthModal from './SelectingMonthModal';
import { getSpecificDateFrom } from 'utils/formatting';

import BookingScheduleHeader from './BookingScheduleHeader';
import ProductFilterModal from './ProductFilterModal';

import { Booking, BookingTermType } from './graphQL/types';
import useQueryGetProductFilterList from './graphQL/hooks/useQueryGetProductFilterList';

import DetailModal from './DetailModal';
import AttendanceCheckModal from './AttendanceCheckModal';
import CanceledScheduleModal from './CanceledScheduleModal';
import BookingCalendarView from './BookingCalendarView';
import BookingListView from './BookingListView';

import { parseQueryString } from 'utils/common';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_BOOKING_TICKETS, GET_FIXED_SCHEDULE_TICKET_LIST } from './graphQL';
import { FixedTicketFilter, UnfixedTicketFilter } from 'types/booking.types';

const BookingScheduleContainer = styled.section<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;

  position: relative;

  width: 100%;
  max-width: 1200px;
  min-height: 100vh;

  padding: 0 20px 64px;

  background-color: #fff;
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 57px - 88px - 28px - 90px - 12px);
`;

const GotoTopButton = styled.div<{ $screenWidth: number; $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  position: fixed;
  z-index: 15;

  padding: 10px;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);

  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: rgba(65, 75, 86, 1);

  background-color: rgba(255, 255, 255, 1);
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);

  cursor: pointer;

  ${({ $isMobile, $screenWidth }) =>
    `
    
    bottom: ${$isMobile ? '87px' : '64px'};
    right: ${
      $isMobile
        ? '20px'
        : $screenWidth
          ? `calc((100% - ${$screenWidth}px)/2)`
          : '20px'
    };

    width: ${$isMobile ? '44px' : '59px'};
    height: ${$isMobile ? '44px' : '64px'};
    border-radius: ${$isMobile ? '50%' : '12px'};
  `}
`;

const TriangleIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid rgba(65, 75, 86, 1);

  border-radius: 2px;
`;

const DeliveryBannerBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  padding: 16px 20px;

  border: 1px solid;
  border-radius: 12px;
  border-color: rgba(0, 102, 255, 0.4);

  background-color: rgba(0, 113, 233, 0.08);

  > div {
    display: flex;
    gap: 10px;
    align-items: center;

    > p {
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: #0071e9;
    }
  }

  > button {
    display: flex;
    align-items: center;
    gap: 2px;

    padding: 0;

    background-color: transparent;

    color: #0071e9;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;

    cursor: pointer;

    > svg {
      transform: rotate(-90deg);
    }
  }
`;

const getFirstDayOfCurrentMonth = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return firstDay.getTime().toString();
};

interface Props {
  needToRefetch: boolean;
  initRefetchStatus: () => void;
  hasDeliveryItem: boolean;
  refetchUnfixedTicketCount: () => void;
}

function BookingScheduleSection({
  needToRefetch,
  initRefetchStatus,
  hasDeliveryItem,
  refetchUnfixedTicketCount,
}: Props) {
  const { width, ref } = useResizeDetector();
  const navigate = useNavigate();

  const location = useLocation();
  const queryString = location.search;

  const [totalTicketCount, setTotalTicketCount] = useState<number>(0);
  const [selectedMonth, setSelectedMonth] = useState<Option>({
    label: new Date().toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
    }),
    value: getFirstDayOfCurrentMonth(),
  });

  const [isSelectingMonthModal, setIsSelectingMonthModal] = useState(false);
  const [isProductFilterModal, setIsProductFilterModal] = useState(false);
  const [isDetailModal, setIsDetailModal] = useState(false);
  const [isAttendanceCheckModal, setIsAttendanceCheckModal] = useState(false);
  const [isCanceledScheduleModal, setIsCanceledScheduleModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState<
    UnfixedTicket | FixedTicket
  >();

  const hostId = useUserInformation(state => state.userInfo.me.host.id);

  const isMobile = useIsMobile();

  const [isLayoutType, setIsLayoutType] = useState<'calendar' | 'list'>(
    'calendar',
  );

  /* 목록 보기: 일정이 끝났는데 아직 예약확정인(사용처리 누락) 예약만 보기 */
  const [onlyPendingUse, setOnlyPendingUse] = useState(false);

  const myInfo: MeData = useUserInformation(state => state.userInfo);
  const hostCreatedAt = dayjs(myInfo.me.host.createdAt); // 호스트 가입한 날짜 === 날짜에서 선택할 수 있는 처음 날짜

  const {
    isLoading,
    bookingTickets,
    ticketFilterInput,
    bookingInfo,
    setTicketFilterInput,
    handleSetTicketFilterInput, // 필터 업데이트
    handleRefetchBookingList, // 재호출
  } = useBookingListHooks();

  let orderSize = 50;

  useEffect(() => {
    if (bookingInfo?.totalCount > orderSize && !isMobile) {
      orderSize = bookingInfo.totalCount;

      setTicketFilterInput(prev => ({
        size: orderSize,
        ...prev,
      }));
    }
  }, [bookingInfo?.totalCount]);

  const handleListType = () => {
    if (isLoading) {
      return;
    }

    setIsLayoutType(prev => (prev === 'calendar' ? 'list' : 'calendar'));
  };

  const handleOnlyPendingUse = () => {
    if (isLoading) {
      return;
    }

    setOnlyPendingUse(prev => !prev);
  };

  const { data: productFilterList } = useQueryGetProductFilterList();

  const mappedBookingDateList = bookingTickets.map(item => {
    const parsedDate = dayjs(item.date, 'YYYY년 M월 D일 dddd');
    const yearMonth = getSpecificDateFrom(item.date, 'YYYY년 M월');

    const startOfMonthTimestamp = parsedDate
      .startOf('month')
      .valueOf()
      .toString();

    return {
      label: yearMonth,
      value: startOfMonthTimestamp,
    };
  });

  const handleSelectingMonthModal = () => {
    if (isLoading) {
      return;
    }

    setIsSelectingMonthModal(prev => !prev);
  };

  /* 월 선택 핸들러 */
  const handleSelectingMonth = (opt: Option) => {
    setSelectedMonth(opt);

    const selectedDate = dayjs(parseInt(opt.value));
    const startOfMonth = selectedDate.startOf('month').valueOf();
    const endOfMonth = selectedDate.endOf('month').valueOf();

    handleSetTicketFilterInput('filter', {
      ...ticketFilterInput.filter,
      termWithType: {
        term: {
          startedAt: startOfMonth,
          endedAt: endOfMonth,
        },
        type: BookingTermType.STARTING,
      },
    });
  };

  const needToCheckAttendance = (item: UnfixedTicket | FixedTicket) => {
    const isUnfixedTicket = 'bookingItem' in item ? true : false;

    /* 
      출석 체크 조건
        1. 예약 확정이 되어야한다.
        2. 당일 && 이벤트 시작 1시간 전부터 출석 체크 가능하다.
    */

    let startTime = 0;
    let endTime = 0;

    if (isUnfixedTicket) {
      /* 조율형 */
      const unfixedTicket = item as UnfixedTicket;
      startTime = unfixedTicket.scheduleTerm?.startedAt;
      endTime = unfixedTicket.scheduleTerm?.endedAt;
    } else {
      /* 지정형 */
      const fixedTicket = item as FixedTicket;
      startTime = fixedTicket.term?.startedAt;
      endTime = fixedTicket.term?.endedAt;
    }

    if (
      dayjs().isAfter(dayjs(startTime).subtract(1, 'hour'))
      // && dayjs().isBefore(dayjs(endTime))
    ) {
      return true;
    }

    return false;
  };

  /* 상품 필터 핸들러 */
  const handleProductFilterModal = () => {
    if (isLoading) {
      return;
    }
    setIsProductFilterModal(prev => !prev);
  };

  const selectItemHandler = (item: UnfixedTicket | FixedTicket) => {
    const isScheduleCanceled = item.status === ScheduleState.CANCELED;

    setSelectedItem(item);

    /* 일정취소된 경우 취소 확인 모달 */
    if (isScheduleCanceled) {
      return handleCanceledScheduleModal();
    }

    /* 출석 체크 모달 열기 */
    if (needToCheckAttendance(item)) {
      return setIsAttendanceCheckModal(true);
    }

    setIsDetailModal(true);
  };

  const handleAttendanceCheckModal = () => {
    setIsAttendanceCheckModal(prev => !prev);
  };

  const handleDetailModal = () => {
    setIsDetailModal(prev => !prev);
  };

  const handleGotoTop = () => {
    document.body.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  /* 이하 모달이 열리고 닫혔을떄, 스크롤 이동 방지를 위한 코드 */
  let ticketId = '';
  const isUnfixedTicket =
    selectedItem && 'bookingItem' in selectedItem ? true : false;

  if (isUnfixedTicket) {
    ticketId = (selectedItem as UnfixedTicket)?.id;
  } else {
    ticketId = (selectedItem as FixedTicket)?.id;
  }

  const scheduleCardBox = document.querySelector(
    `.schedule-card-box.id-${ticketId}`,
  );

  const greatGrandParentElement =
    scheduleCardBox?.parentElement?.parentElement?.parentElement;

  useEffect(() => {
    if (selectedItem && greatGrandParentElement) {
      setTimeout(() => {
        greatGrandParentElement.scrollIntoView({
          block: 'center',
          inline: 'center',
        });
      }, 0);
    }
  }, [selectedItem, isAttendanceCheckModal, isDetailModal]);

  /* 이상 모달이 열리고 닫혔을떄, 스크롤 이동 방지를 위한 코드 */
  const handleCanceledScheduleModal = () => {
    setIsCanceledScheduleModal(prev => !prev);
  };

  const goToDeliveryPage = () => {
    navigate(DELIVERY_PATH);
  };

  useEffect(() => {
    if (needToRefetch) {
      initRefetchStatus();
      handleRefetchBookingList();
    }
  }, [needToRefetch]);

  let mappedBookingTickets = [];

  for (let item of bookingTickets) {
    let tempMap: { [key: string]: (UnfixedTicket | FixedTicket)[] } = {};

    for (let i = 0; i < item.value.length; i++) {
      const ticket = item.value[i];
      const isFixed = 'minimumQuota' in ticket;

      const productId = isFixed
        ? (ticket as FixedTicket).product?.id
        : (ticket as UnfixedTicket).bookingItem?.booking?.info?.product?.id;

      const startTimeStamp = isFixed
        ? (ticket as FixedTicket).term?.startedAt
        : (ticket as UnfixedTicket).scheduleTerm?.startedAt;

      if (!tempMap[`${productId}-${startTimeStamp}`]) {
        tempMap[`${productId}-${startTimeStamp}`] = [];
      }
      tempMap[`${productId}-${startTimeStamp}`].push(ticket);
    }

    Object.values(tempMap).forEach(ticketItem => {
      const isFixed = 'minimumQuota' in ticketItem[0];
      const valueList = [];

      if (isFixed) {
        ticketItem.forEach(ticket => {
          valueList.push({
            ...ticket,
            exposedCounts: ticketItem.length,
          });
        });
      } else {
        valueList.push({
          ...ticketItem[0],
          exposedCounts: ticketItem.length,
        });
      }

      mappedBookingTickets.push({
        date: item.date,
        value: valueList,
      });
    });
  }

  useEffect(() => {
    if (mappedBookingTickets && isLayoutType === 'calendar') {
      const totalTicketCount = mappedBookingTickets.reduce((acc, curr) => {
        return acc + curr.value.length;
      }, 0);

      setTotalTicketCount(totalTicketCount);
    }
  }, [mappedBookingTickets, isLayoutType]);

  const [getUnfixedTicketInfoById] =
    useLazyQuery<BookingTicket>(GET_BOOKING_TICKETS);
  const [getFixedTicketInfoById] = useLazyQuery<FixedBookingTicket>(
    GET_FIXED_SCHEDULE_TICKET_LIST,
  );

  /* 알림톡에 사용될 예약 리스트 필터 가져오기 api */
  const getUnfixedTicketInfo = async (
    ticketId: string,
    filterKey: 'idIn' | 'scheduleIds',
  ) => {
    const { data: ticketData, loading: ticketDataLoading } =
      await getUnfixedTicketInfoById({
        variables: { filter: { [filterKey]: ticketId } },
      });

    if (ticketData) {
      selectItemHandler(ticketData?.booking.tickets.edges[0].node);
    }
  };

  const getFixedTicketInfo = async (scheduleId: string) => {
    const { data: ticketData, loading: ticketDataLoading } =
      await getFixedTicketInfoById({
        variables: {
          page: 1,
          size: 20,
          filter: {
            hostId: hostId,
            ids: scheduleId,
          },
        },
      });

    if (ticketData) {
      selectItemHandler(ticketData?.product.schedules.edges[0].node);
    }
  };

  useEffect(() => {
    if (queryString) {
      const queryParams = parseQueryString(queryString);

      /* 조율형 (ticket_id) */
      if (queryParams && queryParams.ticket_id) {
        getUnfixedTicketInfo(queryParams.ticket_id, 'idIn');
        return;
      }

      /* 지정형 (schedule_id) */
      if (queryParams && queryParams.schedule_id) {
        getFixedTicketInfo(queryParams.schedule_id);
        return;
      }
    }
  }, [queryString]);

  return (
    <BookingScheduleContainer $isMobile={isMobile} ref={ref}>
      {hasDeliveryItem && (
        <DeliveryBannerBox onClick={goToDeliveryPage}>
          <div>
            <IconTruck width="24px" height="24px" />
            <p>배송 상품은 여기에서 관리해요</p>
          </div>

          <button>
            이동하기
            <IconChevronDown />
          </button>
        </DeliveryBannerBox>
      )}

      <BookingScheduleHeader
        selectedMonth={selectedMonth}
        handleSelectingMonthModal={handleSelectingMonthModal}
        handleProductFilterModal={handleProductFilterModal}
        handleSelectingMonth={handleSelectingMonth}
        hostCreatedAt={hostCreatedAt}
        totalBookingCount={totalTicketCount}
        currentFilter={ticketFilterInput.filter}
        handleListType={handleListType}
        listType={isLayoutType}
        isLoading={isLoading}
        onlyPendingUse={onlyPendingUse}
        handleOnlyPendingUse={handleOnlyPendingUse}
      />

      <>
        {isLayoutType === 'calendar' ? (
          isLoading ? (
            <LoadingBox>
              <Loading type="flex" />
            </LoadingBox>
          ) : (
            <BookingCalendarView
              bookingTickets={mappedBookingTickets || []}
              selectItemHandler={selectItemHandler}
              selectedMonth={selectedMonth}
            />
          )
        ) : (
          <BookingListView
            selectItemHandler={selectItemHandler}
            selectedMonth={selectedMonth}
            productIdFilter={ticketFilterInput.filter?.productId}
            onlyPendingUse={onlyPendingUse}
          />
        )}

        {isLayoutType === 'calendar' && (
          <RefreshButton handleRefreshData={handleRefetchBookingList} />
        )}
      </>

      <GotoTopButton
        onClick={handleGotoTop}
        $isMobile={isMobile}
        $screenWidth={width}
      >
        <TriangleIcon />
        {isMobile ? <></> : '맨위로'}
      </GotoTopButton>

      {isSelectingMonthModal && (
        <SelectingMonthModal
          isMobile={isMobile}
          mappedBookingDateList={mappedBookingDateList}
          handleSelectingMonthModal={handleSelectingMonthModal}
          handleSelectingMonth={handleSelectingMonth}
          selectedMonth={selectedMonth}
          hostCreatedAt={hostCreatedAt}
        />
      )}
      {isProductFilterModal && (
        <ProductFilterModal
          isMobile={isMobile}
          handleProductFilterModal={handleProductFilterModal}
          productFilterList={productFilterList}
          currentFilter={ticketFilterInput.filter}
          updateFilter={handleSetTicketFilterInput}
        />
      )}
      {isDetailModal && (
        <DetailModal
          item={selectedItem}
          handleRefetchBookingList={handleRefetchBookingList}
          handleModal={handleDetailModal}
          refetchUnfixedTicketCount={refetchUnfixedTicketCount}
        />
      )}
      {isAttendanceCheckModal && (
        <AttendanceCheckModal
          item={selectedItem}
          handleModal={handleAttendanceCheckModal}
          handleRefetchBookingList={handleRefetchBookingList}
          refetchUnfixedTicketCount={refetchUnfixedTicketCount}
        />
      )}
      {isCanceledScheduleModal && (
        <CanceledScheduleModal
          handleModal={handleCanceledScheduleModal}
          scheduleIds={selectedItem?.id}
        />
      )}
    </BookingScheduleContainer>
  );
}

export default BookingScheduleSection;
